import { useI18N } from "@sundae/react-hooks";
import { Balance, Image, Text, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, memo } from "react";
import { Trans } from "react-i18next";
import { useCalculateContributionOutcome } from "../../../hooks/taste-test/useCalculateContributionOutcome";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useProfileAppContext } from "../../../stores";
import { IAssetMetaData } from "../../../types/Asset.types";
import BalanceWithAssetName from "../../BalanceWithAssetName";
import TextLoadingPlaceholder from "../../TextLoadingPlaceholder";
import TasteTestClaimingTransactionDetails from "../TasteTestClaimingTransactionDetails";

interface ITasteTestClaimingSetupProps {
  isTxDetailsOpen: boolean;
}

const TasteTestClaimingSetup: FC<ITasteTestClaimingSetupProps> = ({ isTxDetailsOpen }) => {
  const { currentLocale, t } = useI18N("taste-test");
  const {
    derived: { latestHistoryRecord },
    state: { reservedUtxos, tasteTest },
  } = useTasteTestContext();
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();

  const { calculateClaimableAmount, isLoadingPoolByLPAssetId } = useCalculateContributionOutcome({
    latestHistoryRecord,
    currentBalance: tasteTest?.currentBalance,
    reservedUtxos,
    tasteTest,
  });

  const { gatheredAssetAmount, suppliedAssetAmount } = calculateClaimableAmount();

  return (
    <div
      className={cx(
        "relative flex w-full flex-col gap-6 pt-48",
        toolkitCx.layout.spacing.both.md,
        toolkitCx.recipes.blurryCorner.primary
      )}
    >
      <Image
        src="/static/images/widgets/claim-now.png"
        alt={t("tasteTest.claimingSetup", { ns: "alt-tags" })}
        className={cx("absolute top-4 h-40", toolkitCx.recipes.center.horizontal)}
      />
      <div className="relative z-10 mt-2 flex w-full flex-col gap-2">
        <Text className="inline-block" tag="h2" size="2xl" weight="medium">
          {t("detail.widget.claim.output.title")}
        </Text>

        <Text className="inline-block" tag="p" size="sm" variant="muted">
          <Trans i18nKey="detail.widget.claim.output.copy" ns="taste-test">
            Great job! You successfully participated in this Taste Test. Your contribution boiled
            down to a total of approximately{" "}
            <Text tag="span" className="inline-block" size="sm" variant="current">
              {isLoadingPoolByLPAssetId ? (
                <TextLoadingPlaceholder size="sm" />
              ) : (
                <Balance
                  abbreviate={false}
                  size="sm"
                  gradient
                  locale={currentLocale}
                  privacy={privacyEnabled}
                  value={gatheredAssetAmount}
                />
              )}
            </Text>{" "}
            and{" "}
            {isLoadingPoolByLPAssetId ? (
              <TextLoadingPlaceholder size="sm" />
            ) : (
              <BalanceWithAssetName
                abbreviate={false}
                className="inline-block"
                asset={suppliedAssetAmount.metadata as unknown as IAssetMetaData}
                size="sm"
                gradient
                showSymbol={false}
                locale={currentLocale}
                privacy={privacyEnabled}
                value={suppliedAssetAmount}
              />
            )}
            . Claim them now by clicking the button below.
          </Trans>
        </Text>
      </div>
      <TasteTestClaimingTransactionDetails isOpen={isTxDetailsOpen} />
    </div>
  );
};

export default memo(TasteTestClaimingSetup);
